/**
 * <ScanDefaultsCard>
 * Settings card for the default quantity applied on every successful
 * barcode scan during invoice / draft creation.
 *
 * Persists via PUT /api/settings/scan-defaults. Admin-only route (the
 * whole Settings page is already gated by admin role at the router).
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ScanLine, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { scanDefaultsAPI } from '../../lib/api';

const ScanDefaultsCard = () => {
    const [qty, setQty] = useState(6);
    const [initial, setInitial] = useState(6);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        scanDefaultsAPI.get()
            .then((r) => {
                const v = r.data?.default_quantity ?? 6;
                setQty(v);
                setInitial(v);
            })
            .catch(() => {});
    }, []);

    const save = async () => {
        const n = Math.max(1, Math.floor(Number(qty) || 0));
        if (n === initial) {
            toast.info('No changes to save');
            return;
        }
        setSaving(true);
        try {
            await scanDefaultsAPI.put(n);
            setInitial(n);
            setQty(n);
            toast.success(`Default scan quantity set to ${n}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const dirty = Number(qty) !== initial;
    return (
        <Card data-testid="scan-defaults-card">
            <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                    <ScanLine size={18} className="text-[#C08478]" />
                    Barcode scan defaults
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-sm text-gray-600">
                    Quantity pre-filled in the confirmation popup after every barcode scan.
                    Salespeople can still edit each scan before adding — this is just the starting number.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-3 items-end">
                    <div>
                        <Label htmlFor="scan-default-qty">Default quantity</Label>
                        <Input
                            id="scan-default-qty"
                            data-testid="scan-default-qty-input"
                            type="number"
                            min={1}
                            step={1}
                            value={qty}
                            onChange={(e) => setQty(e.target.value)}
                            className="mt-1 font-mono"
                        />
                    </div>
                    <Button
                        onClick={save}
                        disabled={!dirty || saving}
                        data-testid="scan-default-qty-save"
                        className="gap-1 bg-[#1A1A1A] hover:bg-[#C08478] text-white md:justify-self-start"
                    >
                        <Save size={14} /> Save
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
};

export default ScanDefaultsCard;
